import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Building2, PenLine, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Sign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null); // null loading, false invalid
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: "", initials: "", signature_text: "", consent: false });

  useEffect(() => {
    fetch(`${API}/sign/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((d) => {
        setInfo(d);
        setForm((f) => ({ ...f, full_name: d.customer_name || "" }));
        if (d.signed) setDone(true);
      })
      .catch(() => setInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please agree to sign electronically.");
    if (!form.full_name.trim() || !form.initials.trim() || !form.signature_text.trim())
      return toast.error("Please complete your name, initials and signature.");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Failed to submit signature");
      }
      setDone(true);
      toast.success("Agreement signed successfully.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (info === null)
    return <div className="min-h-screen flex items-center justify-center bg-background font-mono text-sm text-muted-foreground">Loading your agreement…</div>;

  if (info === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8 text-center">
        <FileText className="h-10 w-10 text-muted-foreground mb-4" />
        <h1 className="text-3xl mb-2">Link not found</h1>
        <p className="text-muted-foreground">This signing link is invalid or has expired. Please contact My Geeks LLC.</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border bg-card flex items-center px-6 sticky top-0 z-50">
        <Building2 className="h-5 w-5 text-primary mr-2" />
        <div>
          <div className="font-display text-lg text-primary leading-none">My Geeks LLC</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Electronic Signature</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 md:p-8 grid lg:grid-cols-2 gap-8">
        {/* Document preview */}
        <div>
          <h2 className="text-lg mb-3">Your Service Agreement</h2>
          <div className="border border-border bg-card h-[70vh]">
            <object data={`${API}/sign/${token}/document`} type="application/pdf" className="w-full h-full" aria-label="Agreement PDF">
              <div className="p-6 text-sm text-muted-foreground">
                Unable to display the PDF here.{" "}
                <a className="text-primary underline" href={`${API}/sign/${token}/document`} target="_blank" rel="noreferrer">
                  Open the agreement in a new tab
                </a>.
              </div>
            </object>
          </div>
        </div>

        {/* Signing panel */}
        <div>
          {done ? (
            <div className="border border-border bg-card p-8 text-center">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h1 className="text-3xl mb-2">Thank you, it's signed!</h1>
              <p className="text-muted-foreground">
                Your agreement for <span className="font-mono">{info.customer_id}</span> has been signed and recorded.
                A copy and audit certificate are now on file with My Geeks LLC.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-border bg-card p-6 space-y-5">
              <div className="flex items-center gap-2">
                <PenLine className="h-5 w-5 text-primary" />
                <h1 className="text-2xl">Sign Your Agreement</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Reference <span className="font-mono text-primary">{info.customer_id}</span>
                {info.plan ? ` · ${info.plan}` : ""}. Please review the document, then complete the fields below.
              </p>

              <div className="space-y-1.5">
                <Label>Full Legal Name</Label>
                <Input data-testid="sign-full-name" className="rounded-none" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} required />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Initials</Label>
                  <Input data-testid="sign-initials" className="rounded-none" maxLength={5} value={form.initials} onChange={(e) => setForm((f) => ({ ...f, initials: e.target.value }))} required />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Type Your Signature</Label>
                  <Input data-testid="sign-signature" className="rounded-none" value={form.signature_text} onChange={(e) => setForm((f) => ({ ...f, signature_text: e.target.value }))} required />
                </div>
              </div>

              {form.signature_text && (
                <div className="border border-border bg-secondary p-4">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Signature preview</div>
                  <div className="font-signature text-4xl text-foreground">{form.signature_text}</div>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="sign-consent" checked={form.consent} onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))} className="mt-0.5 rounded-none" />
                <span className="text-sm text-muted-foreground">
                  I agree to use an electronic signature and confirm that the initials and signature above are mine and legally binding.
                </span>
              </label>

              <Button type="submit" data-testid="sign-submit-btn" className="w-full rounded-none" disabled={submitting}>
                {submitting ? "Submitting…" : "Adopt & Sign"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
